import React from 'react';
import { ActionIcon, Menu, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useI18n } from '../../../hooks/useI18n';
import { IconCheck } from '@tabler/icons-react';
import { GlobeIcon, Icon } from '../Icon';
import { AppTooltip } from '../AppTooltip';
import { Z_INDEX_DROPDOWN } from '@/constants/zIndex';
import { useGeoMarkupStore } from '@/store';
import { usersService } from '@/services/api';
import styles from './LanguageSelector.module.css';

interface LanguageSelectorProps {
  /**
   * Size of the selector button
   * @default 'lg'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether to show language names alongside flags
   * @default false
   */
  showLabels?: boolean;

  /**
   * Custom aria-label for accessibility
   */
  'aria-label'?: string;
}

/**
 * Language codes for visual representation
 */
const languageCodes: Record<string, string> = {
  en: 'EN',
  pl: 'PL',
};

/**
 * Language country names for display
 */
const languageCountries: Record<string, string> = {
  en: 'United States', // English
  pl: 'Poland', // Polish
};

/**
 * Language names for display
 */
const languageNames: Record<string, string> = {
  en: 'English',
  pl: 'Polski',
};

/**
 * Professional Language Selector Component
 *
 * A dropdown-style language selector that follows Mantine's ActionIcon pattern.
 * Provides visual language flags and proper accessibility features.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  size = 'lg',
  showLabels = false,
  'aria-label': ariaLabel,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { t } = useTranslation();
  const {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    getLanguageDisplayName,
    isReady,
  } = useI18n();
  const isAuthenticated = useGeoMarkupStore(
    (state) => state.auth.isAuthenticated
  );
  const updateUserPreferences = useGeoMarkupStore(
    (state) => state.auth.updateUserPreferences
  );

  const handleLanguageChange = async (language: string) => {
    if (language === currentLanguage) return;

    try {
      // Apply local change first
      await changeLanguage(language);

      // Sync to backend if authenticated
      if (isAuthenticated && (language === 'en' || language === 'pl')) {
        usersService
          .updatePreferences({ preferredLanguage: language })
          .then(() => updateUserPreferences({ preferredLanguage: language }))
          .catch(console.error);
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  if (!isReady) {
    return (
      <ActionIcon
        variant="default"
        size={size}
        loading
        aria-label={t('ui.languageSelector.loadingLanguageSelector')}
      >
        <GlobeIcon size={size === 'sm' ? 16 : size === 'md' ? 20 : 20} />
      </ActionIcon>
    );
  }

  const currentName =
    languageNames[currentLanguage] || getLanguageDisplayName(currentLanguage);

  return (
    <Menu
      shadow="md"
      width={showLabels ? 180 : 140}
      position="bottom-end"
      offset={4}
      zIndex={Z_INDEX_DROPDOWN}
      transitionProps={{ duration: 150, transition: 'fade' }}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
      classNames={{
        dropdown: styles.dropdownMenu,
      }}
    >
      <Menu.Target>
        <AppTooltip
          labelKey="tooltips.language.current"
          values={{ language: currentName }}
          position="bottom"
          openDelay={500}
          disabled={isMenuOpen}
        >
          <ActionIcon
            variant="subtle"
            size={size}
            aria-label={
              ariaLabel ||
              t('tooltips.language.current', { language: currentName })
            }
            className={styles.languageButton}
            classNames={{ icon: styles.languageIcon }}
          >
            <div className={styles.languageIndicator}>
              <span className={styles.languageCode}>
                {languageCodes[currentLanguage] ||
                  currentLanguage.toUpperCase()}
              </span>
              <Icon type="chevron-down" size={14} className={styles.chevron} />
            </div>
          </ActionIcon>
        </AppTooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label className={styles.menuLabel}>
          {t('language.select')}
        </Menu.Label>
        {supportedLanguages.map((language) => {
          const name =
            languageNames[language] || getLanguageDisplayName(language);
          const country = languageCountries[language];
          const isActive = language === currentLanguage;

          return (
            <Menu.Item
              key={language}
              onClick={() => handleLanguageChange(language)}
              className={isActive ? styles.activeMenuItem : undefined}
              leftSection={
                <div className={styles.menuLanguageCode}>
                  {languageCodes[language] || language.toUpperCase()}
                </div>
              }
              rightSection={
                isActive ? (
                  <IconCheck size={14} className={styles.checkIcon} />
                ) : undefined
              }
            >
              <div className={styles.languageOption}>
                <Text
                  size="sm"
                  fw={isActive ? 600 : 400}
                  className={styles.languageName}
                >
                  {name}
                </Text>
                {showLabels && country && (
                  <Text size="xs" c="dimmed" className={styles.countryName}>
                    {country}
                  </Text>
                )}
              </div>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
